import type { LongDroughtEvent } from '../../analytics/long-droughts.ts';
import { formatDecimal, formatInteger } from '../../shared/format.ts';

export function LongDroughtSpacingChart(props: { events: readonly LongDroughtEvent[] }) {
  const points = props.events.filter((event) => event.roundsSincePreviousStart !== null);
  if (points.length === 0) {
    return <p className="text-sm text-muted">No observed spacing intervals at this threshold.</p>;
  }
  const maxSpacing = Math.max(...points.map((event) => event.roundsSincePreviousStart ?? 0), 1);
  const left = 44;
  const top = 12;
  const height = 140;
  const plotWidth = Math.max(240, points.length * 28);
  const width = left + plotWidth + 12;
  const xFor = (index: number) =>
    left + ((index + 0.5) / points.length) * plotWidth;
  const yFor = (value: number) => top + height - (value / maxSpacing) * height;

  return (
    <div className="overflow-x-auto">
      <svg
        role="img"
        aria-label="Spacing between long droughts"
        width={width}
        height={top + height + 28}
        className="text-ink"
      >
        <title>Spacing between long droughts</title>
        <desc>Start-to-start round spacing between consecutive long drought events. No trend line.</desc>
        <line x1={left} y1={top} x2={left} y2={top + height} stroke="currentColor" strokeOpacity="0.25" />
        <line
          x1={left}
          y1={top + height}
          x2={left + plotWidth}
          y2={top + height}
          stroke="currentColor"
          strokeOpacity="0.25"
        />
        <text x={4} y={top + 8} className="fill-muted" fontSize="10">
          {formatInteger(maxSpacing)}
        </text>
        {points.map((event, index) => {
          const x = xFor(index);
          const y = yFor(event.roundsSincePreviousStart ?? 0);
          return (
            <g key={event.eventIndex}>
              <line x1={x} y1={top + height} x2={x} y2={y} stroke="#3f5d56" strokeOpacity="0.4" />
              <circle cx={x} cy={y} r="4" fill="#3f5d56">
                <title>
                  {`Current drought length: ${formatInteger(event.lengthRounds)}\nPrevious drought length: ${formatInteger(event.previousLengthRounds)}\nRounds apart: ${formatInteger(event.roundsSincePreviousStart)}\nHours apart: ${formatDecimal(event.hoursSincePreviousStart, 1)}`}
                </title>
              </circle>
              <text x={x} y={top + height + 14} textAnchor="middle" className="fill-muted" fontSize="9">
                {event.eventIndex}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="mt-1 text-xs text-muted">
        X: long drought event number · Y: rounds since previous long drought start
      </p>
    </div>
  );
}
